import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useRealtime } from '../contexts/RealtimeContext';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { FileViewerModal } from '../components/modals/FileViewerModal';
import { JobArtifact } from '@kaizen/shared';
import { formatBytes, formatDate } from '../lib/utils';
import {
  kaizenMicroTransition,
  kaizenStateTransition
} from '../lib/kaizenMotion';
import {
  Files,
  FileText,
  Eye,
  Download,
  Copy,
  Check,
  Bot,
  Shield,
  RefreshCw,
  Loader2,
  WifiOff,
  Radio
} from 'lucide-react';

const INITIAL_ARTIFACTS: JobArtifact[] = [
  {
    id: 'art-1',
    jobId: 'job-diag-101',
    projectId: '7d84a284-06c6-4a1c-88b7-11883085930c',
    filename: 'BCC_DIAGNOSTIC_REPORT.txt',
    localPath: 'D:\\Enabling Work Module\\kaizen-enabling-works-web\\BCC_DIAGNOSTIC_REPORT.txt',
    mimeType: 'text/plain',
    size: 2450,
    contentPreview: `============================================================\nBCC REVIEW SUITE - SYSTEM DIAGNOSTIC REPORT\nDate: 2025-01-01 10:15:32\nNode: v20.10.0 | PowerShell: 7.4.1\nWorking Directory: D:\\Enabling Work Module\\kaizen-enabling-works-web\n\nGit Status: Clean (branch: main, commit: 7f3a9e1)\nBuild Dependencies: OK (all peer dependencies resolved)\nCloudflare Pages Status: Linked (kaizen-enabling-works)\nLocal Ports: 5173 available\nVerification: ALL CHECKS PASSED\n============================================================`,
    createdAt: new Date(Date.now() - 7200000).toISOString()
  },
  {
    id: 'art-2',
    jobId: 'job-build-202',
    projectId: '7d84a284-06c6-4a1c-88b7-11883085930c',
    filename: 'BUILD_SUMMARY.log',
    localPath: 'D:\\Enabling Work Module\\kaizen-enabling-works-web\\BUILD_SUMMARY.log',
    mimeType: 'text/plain',
    size: 1120,
    contentPreview: `vite v5.2.8 building for production...\n✓ 42 modules transformed.\ndist/index.html                   0.48 kB │ gzip:  0.31 kB\ndist/assets/index-Bf9Q2.css       8.41 kB │ gzip:  2.15 kB\ndist/assets/index-D7h0k.js      142.12 kB │ gzip: 44.89 kB\n✓ built in 1.45s\nStatus: Build Completed with exit code 0`,
    createdAt: new Date(Date.now() - 3600000).toISOString()
  }
];

export const FilesPage: React.FC = () => {
  const navigate = useNavigate();
  const { displayPath } = usePrivacy();
  const { getArtifactContent, deviceStatus } = useRealtime();

  const [artifacts, setArtifacts] = useState<JobArtifact[]>(INITIAL_ARTIFACTS);
  const [loadingList, setLoadingList] = useState<boolean>(false);
  const [selectedArtifact, setSelectedArtifact] = useState<JobArtifact | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  // Per-item action state
  const [copiedPathId, setCopiedPathId] = useState<string | null>(null);
  const [copiedTextId, setCopiedTextId] = useState<string | null>(null);
  const [copyingTextId, setCopyingTextId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ id: string; message: string } | null>(null);

  const fetchArtifacts = useCallback(async () => {
    if (!isSupabaseConfigured) return;
    setLoadingList(true);
    try {
      const { data, error } = await supabase
        .from('kr_job_artifacts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (!error && data && data.length > 0) {
        const mapped: JobArtifact[] = data.map((row: any) => ({
          id: row.id,
          jobId: row.job_id,
          projectId: row.project_id || undefined,
          filename: row.filename,
          localPath: row.local_path,
          mimeType: row.mime_type || 'text/plain',
          size: row.size || 0,
          contentPreview: row.content_preview || undefined,
          createdAt: row.created_at
        }));
        setArtifacts(mapped);
      }
    } catch {
      // Keep existing list on failure
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    void fetchArtifacts();
  }, [fetchArtifacts]);

  const handleCopyPath = (art: JobArtifact) => {
    navigator.clipboard.writeText(art.localPath);
    setCopiedPathId(art.id);
    setTimeout(() => setCopiedPathId(null), 2000);
  };

  const handleCopyText = async (art: JobArtifact) => {
    setCopyingTextId(art.id);
    setActionError(null);

    try {
      if (deviceStatus === 'online') {
        const res = await getArtifactContent(art.id);
        if (res.success && typeof res.content === 'string') {
          await navigator.clipboard.writeText(res.content);
          setCopiedTextId(art.id);
          setTimeout(() => setCopiedTextId(null), 2000);
          return;
        }
      }

      // Fallback to preview if offline or agent retrieval returned error
      if (art.contentPreview) {
        await navigator.clipboard.writeText(art.contentPreview);
        setCopiedTextId(art.id);
        setTimeout(() => setCopiedTextId(null), 2000);
      } else {
        setActionError({ id: art.id, message: 'Agent offline and no preview available' });
        setTimeout(() => setActionError(null), 3000);
      }
    } catch (err: unknown) {
      if (art.contentPreview) {
        await navigator.clipboard.writeText(art.contentPreview);
        setCopiedTextId(art.id);
        setTimeout(() => setCopiedTextId(null), 2000);
      } else {
        const msg = err instanceof Error ? err.message : 'Copy failed';
        setActionError({ id: art.id, message: msg });
        setTimeout(() => setActionError(null), 3000);
      }
    } finally {
      setCopyingTextId(null);
    }
  };

  const handleDownload = async (art: JobArtifact) => {
    setDownloadingId(art.id);
    setActionError(null);

    try {
      let contentToDownload: string | undefined = undefined;

      if (deviceStatus === 'online') {
        const res = await getArtifactContent(art.id);
        if (res.success && typeof res.content === 'string') {
          contentToDownload = res.content;
        }
      }

      if (!contentToDownload && art.contentPreview) {
        contentToDownload = art.contentPreview;
      }

      if (!contentToDownload) {
        setActionError({ id: art.id, message: 'Agent offline and no content available' });
        setTimeout(() => setActionError(null), 3000);
        return;
      }

      const mime = art.mimeType || 'text/plain;charset=utf-8';
      const blob = new Blob([contentToDownload], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = art.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Download failed';
      setActionError({ id: art.id, message: msg });
      setTimeout(() => setActionError(null), 3000);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSendToAssistant = (_art: JobArtifact) => {
    navigate('/assistant');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Files className="w-5 h-5 text-indigo-600" />
            <span>Files & Reports</span>
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Reports, diagnostics, and build artifacts generated by host execution jobs
          </p>
        </div>

        <div className="flex items-center gap-2">
          {deviceStatus === 'online' ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-xs border border-emerald-200">
              <Radio className="w-3.5 h-3.5 text-emerald-500 animate-pulse" />
              <span>Agent Live</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs border border-amber-200">
              <WifiOff className="w-3.5 h-3.5 text-amber-500" />
              <span>Agent Offline</span>
            </div>
          )}

          <button
            onClick={() => void fetchArtifacts()}
            disabled={loadingList}
            className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600 transition-colors disabled:opacity-40"
            title="Refresh list"
          >
            <RefreshCw className={`w-4 h-4 ${loadingList ? 'animate-spin' : ''}`} />
          </button>

          <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 text-xs border border-slate-200">
            <Shield className="w-3.5 h-3.5 text-slate-400" />
            <span>Allowed Roots Enforced</span>
          </div>
        </div>
      </div>

      {/* Mobile Card View (Samsung Android Chrome optimized) */}
      <div className="block md:hidden space-y-3">
        <AnimatePresence initial={true} mode="popLayout">
          {artifacts.map((art, index) => (
            <motion.div
              key={art.id}
              layout="position"
              initial={{
                opacity: 0,
                y: 10,
                scale: 0.99
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }}
              exit={{
                opacity: 0,
                y: -6,
                scale: 0.99
              }}
              transition={{
                ...kaizenStateTransition,
                delay: Math.min(
                  index * 0.025,
                  0.12
                )
              }}
              className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm space-y-3"
            >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
                  <FileText className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-mono font-semibold text-xs text-slate-900 truncate">
                    {art.filename}
                  </h4>
                  <p className="text-[10px] text-slate-500 font-mono">
                    {formatBytes(art.size)} • {art.mimeType}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleCopyPath(art)}
                className="p-2 text-slate-400 hover:text-slate-700 rounded-lg border border-slate-100 hover:bg-slate-50 shrink-0 min-h-[40px] min-w-[40px] flex items-center justify-center"
                title="Copy Path"
              >
                <AnimatePresence
                  mode="wait"
                  initial={false}
                >
                  <motion.span
                    key={
                      copiedPathId === art.id
                        ? 'copied'
                        : 'copy'
                    }
                    initial={{
                      opacity: 0,
                      scale: 0.72
                    }}
                    animate={{
                      opacity: 1,
                      scale: 1
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.82
                    }}
                    transition={
                      kaizenMicroTransition
                    }
                    className="inline-flex"
                  >
                    {copiedPathId === art.id ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </motion.span>
                </AnimatePresence>
              </button>
            </div>

            <div className="bg-slate-50 rounded-lg p-2 text-[11px] font-mono text-slate-600 space-y-1">
              <div className="truncate">
                <span className="text-slate-400">Path: </span>
                {displayPath(art.localPath)}
              </div>
              <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 border-t border-slate-200/60">
                <span>Job: {art.jobId}</span>
                <span>{formatDate(art.createdAt)}</span>
              </div>
            </div>

            {actionError && actionError.id === art.id && (
              <p className="text-[11px] text-rose-600 font-medium">
                {actionError.message}
              </p>
            )}

            {/* Mobile Touch Action Buttons (Min height 44px for thumb tap) */}
            <div className="grid grid-cols-3 gap-2 pt-1">
              <button
                onClick={() => {
                  setSelectedArtifact(art);
                  setIsViewerOpen(true);
                }}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-xs font-medium text-slate-700 shadow-sm active:scale-95 transition-all min-h-[44px]"
              >
                <Eye className="w-3.5 h-3.5 text-indigo-600" />
                <span>View</span>
              </button>

              <button
                onClick={() => void handleCopyText(art)}
                disabled={copyingTextId === art.id}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-medium border shadow-sm active:scale-95 transition-all min-h-[44px] ${
                  copiedTextId === art.id
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {copyingTextId === art.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />
                ) : copiedTextId === art.id ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="font-semibold">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    <span>Copy</span>
                  </>
                )}
              </button>

              <button
                onClick={() => void handleDownload(art)}
                disabled={downloadingId === art.id}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-medium shadow-sm active:scale-95 transition-all min-h-[44px] disabled:opacity-50"
              >
                {downloadingId === art.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                <span>Save</span>
              </button>
            </div>
          </motion.div>
        ))}
        </AnimatePresence>
      </div>

      {/* Desktop Artifacts Table */}
      <div className="acrylic-panel overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
                <th className="px-5 py-3">File</th>
                <th className="px-5 py-3">Local Path</th>
                <th className="px-5 py-3">Size</th>
                <th className="px-5 py-3">Source Job</th>
                <th className="px-5 py-3">Generated</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <AnimatePresence initial={true}>
                {artifacts.map((art, index) => (
                  <motion.tr
                    key={art.id}
                    initial={{
                      opacity: 0
                    }}
                    animate={{
                      opacity: 1
                    }}
                    exit={{
                      opacity: 0
                    }}
                    transition={{
                      ...kaizenStateTransition,
                      delay: Math.min(
                        index * 0.018,
                        0.10
                      )
                    }}
                    className="hover:bg-slate-50/60 transition-colors"
                  >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
                        <FileText className="w-4 h-4" />
                      </div>
                      <span className="font-mono font-medium text-slate-900">{art.filename}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-slate-600 truncate max-w-xs" title={art.localPath}>
                    {displayPath(art.localPath)}
                  </td>
                  <td className="px-5 py-3 text-slate-600 font-mono">
                    {formatBytes(art.size)}
                  </td>
                  <td className="px-5 py-3 font-mono text-indigo-600">
                    {art.jobId}
                  </td>
                  <td className="px-5 py-3 text-slate-500">
                    {formatDate(art.createdAt)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {/* Copy Path */}
                      <button
                        onClick={() => handleCopyPath(art)}
                        className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
                        title="Copy Local Path"
                      >
                        <AnimatePresence
                          mode="wait"
                          initial={false}
                        >
                          <motion.span
                            key={
                              copiedPathId === art.id
                                ? 'copied'
                                : 'copy'
                            }
                            initial={{
                              opacity: 0,
                              scale: 0.72
                            }}
                            animate={{
                              opacity: 1,
                              scale: 1
                            }}
                            exit={{
                              opacity: 0,
                              scale: 0.82
                            }}
                            transition={
                              kaizenMicroTransition
                            }
                            className="inline-flex"
                          >
                            {copiedPathId === art.id ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </motion.span>
                        </AnimatePresence>
                      </button>

                      {/* View */}
                      <button
                        onClick={() => {
                          setSelectedArtifact(art);
                          setIsViewerOpen(true);
                        }}
                        className="flex items-center gap-1 px-2.5 py-1 text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded text-xs font-medium transition-colors shadow-xs"
                      >
                        <Eye className="w-3 h-3 text-indigo-600" />
                        <span>View</span>
                      </button>

                      {/* Copy Text */}
                      <button
                        onClick={() => void handleCopyText(art)}
                        disabled={copyingTextId === art.id}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
                          copiedTextId === art.id
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 shadow-xs'
                        }`}
                        title="Copy full text content to clipboard"
                      >
                        {copyingTextId === art.id ? (
                          <Loader2 className="w-3 h-3 animate-spin text-indigo-600" />
                        ) : copiedTextId === art.id ? (
                          <>
                            <Check className="w-3 h-3 text-emerald-600" />
                            <span>Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>Copy Text</span>
                          </>
                        )}
                      </button>

                      {/* Download */}
                      <button
                        onClick={() => void handleDownload(art)}
                        disabled={downloadingId === art.id}
                        className="flex items-center gap-1 px-2.5 py-1 text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded text-xs font-medium transition-colors shadow-xs disabled:opacity-50"
                        title="Download artifact file"
                      >
                        {downloadingId === art.id ? (
                          <Loader2 className="w-3 h-3 animate-spin text-indigo-600" />
                        ) : (
                          <Download className="w-3 h-3 text-slate-600" />
                        )}
                        <span>Download</span>
                      </button>

                      {/* Send to JARVIS */}
                      <button
                        onClick={() => handleSendToAssistant(art)}
                        className="flex items-center gap-1 px-2.5 py-1 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded text-xs font-medium transition-colors"
                        title="Send report to Assistant"
                      >
                        <Bot className="w-3 h-3" />
                        <span>To JARVIS</span>
                      </button>
                    </div>
                  </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>

      {/* File Viewer Modal */}
      <FileViewerModal
        artifact={selectedArtifact}
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        onSendToAssistant={handleSendToAssistant}
      />
    </div>
  );
};
